import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LineChart } from '@mui/x-charts/LineChart';
import { useTranslation } from 'react-i18next';

import { ROUTES } from '../../../constants/routes';
import { useSessionMetricsQuery } from '../../../hooks/useDashboardQueries';
import { ErrorMessage } from '../../../components/common/ErrorMessage/ErrorMessage';
import { MobileBottomNav } from '../Stations/MobileBottomNav';
import { SessionMetricSample } from '../../../services/ocpp/types';
import { SessionStatus } from '../../../shared/enums/status';
import { formatDateTime } from '../../../shared/utils/dateFormat';
import { formatUAH } from '../../../shared/utils/currency';
import { CurrentLimitControl } from './CurrentLimitControl';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSION } from '../../../shared/utils/permissions';
import './session-metrics.css';

const PHASE_COLORS: Record<string, string> = {
  L1: '#2563eb',
  L2: '#d97706',
  L3: '#dc2626',
  'L1-N': '#2563eb',
  'L2-N': '#d97706',
  'L3-N': '#dc2626',
  total: '#10b981',
};

function collectKeys(
  samples: SessionMetricSample[],
  field: 'voltage' | 'current' | 'power',
): string[] {
  const keys = new Set<string>();
  for (const s of samples) {
    Object.keys(s[field] ?? {}).forEach((k) => keys.add(k));
  }
  return Array.from(keys).sort();
}

interface SeriesChartProps {
  title: string;
  unit: string;
  field: 'voltage' | 'current' | 'power';
  samples: SessionMetricSample[];
}

function SeriesChart({ title, unit, field, samples }: SeriesChartProps) {
  const { t } = useTranslation();
  const keys = useMemo(() => collectKeys(samples, field), [samples, field]);
  const xData = useMemo(
    () => samples.map((s) => new Date(s.timestamp)),
    [samples],
  );
  const series = useMemo(
    () =>
      keys.map((key) => ({
        label: `${key} (${unit})`,
        color: PHASE_COLORS[key] ?? '#64748b',
        data: samples.map((s) => {
          const v = s[field]?.[key];
          return typeof v === 'number' ? v : null;
        }),
        showMark: false,
        connectNulls: true,
      })),
    [keys, samples, field, unit],
  );

  const emptyText =
    samples.length === 0
      ? t('dashboard.tables.sessions.metricsNoSamples')
      : keys.length === 0
        ? t('dashboard.tables.sessions.metricsNoMeasurands', { field })
        : null;

  return (
    <section className="session-metrics-card">
      <h3 className="session-metrics-card-title">
        {title}
        {!emptyText && <span className="session-metrics-unit"> ({unit})</span>}
      </h3>
      {emptyText ? (
        <p className="session-metrics-empty">{emptyText}</p>
      ) : (
        <div className="session-metrics-chart">
          <LineChart
            xAxis={[
              {
                data: xData,
                scaleType: 'time',
                // Chart ticks keep seconds — samples arrive seconds apart.
                valueFormatter: (v: Date) =>
                  v.toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  }),
              },
            ]}
            series={series}
            height={280}
            margin={{ left: 56, right: 16, top: keys.length > 1 ? 40 : 16, bottom: 40 }}
            grid={{ horizontal: true }}
            slotProps={{
              legend: {
                hidden: keys.length <= 1,
                direction: 'row',
                position: { vertical: 'top', horizontal: 'right' },
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                itemGap: 12,
                labelStyle: { fontSize: 12, fill: '#475569' },
              },
            }}
          />
        </div>
      )}
    </section>
  );
}

interface MetaItem {
  label: string;
  value: string | number;
}

export const SessionMetrics = () => {
  const { sessionId: sessionIdParam } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { has } = usePermissions();
  const canManageSessions = has(PERMISSION.SESSIONS_MANAGE);
  const sessionId = Number(sessionIdParam);
  const valid = Number.isFinite(sessionId) && sessionId > 0;

  const { data, isLoading, isFetching, error, refetch } = useSessionMetricsQuery({
    sessionId: valid ? sessionId : null,
  });

  const samples = useMemo(() => data?.samples ?? [], [data]);

  const showLoading = isLoading || isFetching;
  const errorMessage =
    error instanceof Error ? error.message : t('dashboard.tables.sessions.metricsErrorLoading');

  const summary = useMemo(() => {
    if (samples.length === 0) return null;
    const last = samples[samples.length - 1];
    return {
      sampleCount: samples.length,
      latestSoc: last.soc,
      latestTemperature: last.temperature,
      latestMeterValueKwh:
        typeof last.meterValue === 'number' ? last.meterValue / 1000 : undefined,
      latestCurrentOffered: last.currentOffered,
    };
  }, [samples]);

  const metaItems = useMemo<MetaItem[]>(() => {
    if (!data) return [];
    const meta = (labelKey: string, value: string | number): MetaItem => ({
      label: t(`dashboard.tables.sessions.${labelKey}`),
      value,
    });
    return [
      meta('metricsStation', data.stationIdTag),
      meta('metricsConnector', data.connectorId ?? '—'),
      meta('metricsStatus', data.chargingStatus),
      meta('metricsStarted', data.startedAt ? formatDateTime(data.startedAt) : '—'),
      meta('metricsFinished', data.finishedAt ? formatDateTime(data.finishedAt) : '—'),
      ...(data.idleStartedAt ? [meta('metricsIdleStarted', formatDateTime(data.idleStartedAt))] : []),
      ...(data.idleEndedAt ? [meta('metricsIdleEnded', formatDateTime(data.idleEndedAt))] : []),
      ...(data.idleAmount != null ? [meta('metricsIdleRevenue', formatUAH(data.idleAmount))] : []),
      meta('metricsSamples', summary?.sampleCount ?? 0),
      ...(summary?.latestSoc != null ? [meta('metricsSoC', `${summary.latestSoc.toFixed(0)}%`)] : []),
      ...(summary?.latestTemperature != null
        ? [meta('metricsTemp', `${summary.latestTemperature.toFixed(1)} °C`)]
        : []),
      ...(summary?.latestMeterValueKwh != null
        ? [meta('metricsEnergy', `${summary.latestMeterValueKwh.toFixed(2)} kWh`)]
        : []),
    ];
  }, [data, summary, t]);

  // Changing the current limit acts on a driver's charge: sessions.manage.
  const limitControlNode =
    data &&
    canManageSessions &&
    (data.chargingStatus === SessionStatus.CHARGING ||
      data.chargingStatus === SessionStatus.PAUSED) ? (
      <CurrentLimitControl
        stationIdTag={data.stationIdTag}
        connectorId={data.connectorId ? Number(data.connectorId) : 1}
        initialAmps={summary?.latestCurrentOffered}
      />
    ) : null;

  // Shared between the desktop and mobile trees (CSS decides which shows).
  const fallbackNode = !valid ? (
    <ErrorMessage message={t('dashboard.tables.sessions.metricsInvalidId', { id: sessionIdParam })} />
  ) : error ? (
    <ErrorMessage message={errorMessage} onRetry={() => refetch()} />
  ) : null;

  const chartsNode = (
    <>
      {limitControlNode}
      <SeriesChart
        title={t('dashboard.tables.sessions.metricsVoltage')}
        unit="V"
        field="voltage"
        samples={samples}
      />
      <SeriesChart
        title={t('dashboard.tables.sessions.metricsCurrent')}
        unit="A"
        field="current"
        samples={samples}
      />
      <SeriesChart
        title={t('dashboard.tables.sessions.metricsActivePower')}
        unit="W"
        field="power"
        samples={samples}
      />
    </>
  );

  const goBack = () => navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_SESSIONS}`);

  return (
    <>
      <div className="desktop-layout">
        <div className="filter-bar">
          <div className="filter-bar-inner">
            <div className="filter-controls">
              <button
                type="button"
                className="back-btn"
                onClick={goBack}
                aria-label={t('dashboard.tables.sessions.metricsBackAria')}
              >
                <span className="material-symbols-outlined" aria-hidden="true">arrow_back</span>
              </button>
              <h2 className="metrics-page-title">
                {t('dashboard.tables.sessions.metricsTitle', { id: sessionIdParam })}
              </h2>
            </div>
            <button
              type="button"
              className="refresh-btn"
              onClick={() => refetch()}
              aria-label={t('dashboard.tables.sessions.metricsRefreshAria')}
            >
              <span className="material-symbols-outlined" aria-hidden="true">refresh</span>
            </button>
          </div>
        </div>

        <div
          className={`content-inner${showLoading ? ' loading-dim' : ''}`}
          style={{ padding: '2.5rem' }}
        >
          {fallbackNode ?? (
            <>
              {metaItems.length > 0 && (
                <div className="session-metrics-meta">
                  {metaItems.map((item) => (
                    <div key={item.label}>
                      <span className="session-metrics-meta-label">{item.label}</span>
                      <span className="session-metrics-meta-value">{item.value}</span>
                    </div>
                  ))}
                </div>
              )}
              {chartsNode}
            </>
          )}
        </div>
      </div>

      <div className="mobile-layout">
        <header className="mobile-header">
          <div className="mobile-header-top">
            <button
              type="button"
              className="mobile-back-btn"
              onClick={goBack}
              aria-label={t('dashboard.tables.sessions.metricsBackAria')}
            >
              <span className="material-symbols-outlined" aria-hidden="true">arrow_back</span>
            </button>
            <h1 className="mobile-header-title">
              {t('dashboard.tables.sessions.metricsTitleShort', { id: sessionIdParam })}
            </h1>
            <button
              type="button"
              className="mobile-refresh-btn"
              onClick={() => refetch()}
              aria-label={t('dashboard.tables.sessions.metricsRefreshAria')}
            >
              <span className="material-symbols-outlined" aria-hidden="true">refresh</span>
            </button>
          </div>
        </header>

        <main className="mobile-content">{fallbackNode ?? chartsNode}</main>

        <MobileBottomNav />
      </div>
    </>
  );
};

export default SessionMetrics;
